/* ════════════════════════════════════════════════════════════════════
   Wrenshelf — product page behaviour.

   What this file deliberately does NOT do:

   · It never multiplies, rounds or formats money. Every price the widget
     can ever show — four tiers, each with and without the add-on — was
     formatted by Liquid's `money` filter and parked in a data attribute.
     Switching tiers copies a finished string. That is the only way the
     page can stay correct under Shopify Markets, where the customer's
     currency is not the shop's, and it is why the prototype's exchange
     rate table had to go: it disagreed with checkout.

   · It never rebuilds the tier list. The prototype re-rendered #tiers
     with innerHTML on every selection, which destroyed the element the
     keyboard was standing on and threw focus back to <body>. Here the
     radios are real, the browser owns selection and arrow keys, and JS
     only writes into text nodes that already exist.

   · It never fakes a discount. A tier adds quantity N and records which
     tier was chosen in a line item property. The saving itself is an
     automatic discount configured in the Shopify admin.
   ════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* Both main-product and related-products request this file; whichever
     lands first wins and the other tag is a no-op. */
  if (window.__wrenshelfProductLoaded) return;
  window.__wrenshelfProductLoaded = true;

  var TOAST_MS = 2400;

  /* ---------- small helpers ---------- */

  function setText(el, value) {
    if (el && value != null && el.textContent !== value) el.textContent = value;
  }

  function fill(template, token, value) {
    return String(template || '').split(token).join(value);
  }

  function toast(message) {
    if (!message) return;
    var box = document.getElementById('toast');
    var msg = document.getElementById('toast-msg');
    if (!box || !msg) return;
    msg.textContent = message;
    box.classList.add('show');
    clearTimeout(toast._t);
    toast._t = setTimeout(function () { box.classList.remove('show'); }, TOAST_MS);
  }

  /* The line item property name lives in the input's own name attribute
     — properties[Bundle] — so the translated label is never duplicated
     into a second data attribute that could drift out of step. */
  function propertyName(input) {
    if (!input || !input.name) return null;
    var open = input.name.indexOf('[');
    var close = input.name.lastIndexOf(']');
    if (open === -1 || close <= open) return null;
    return input.name.slice(open + 1, close);
  }

  /* ---------- the buy box ---------- */

  function initProduct(root) {
    if (root.__wrenshelfBound) return;
    root.__wrenshelfBound = true;

    var form = root.querySelector('[data-product-add]');
    var status = root.querySelector('[data-form-status]');
    var priceEl = root.querySelector('[data-price-current]');
    var compareEl = root.querySelector('[data-price-compare]');
    var compareVal = root.querySelector('[data-price-compare-value]');
    var saveEl = root.querySelector('[data-price-save]');
    var addBtn = root.querySelector('[data-add-button]');
    var addBtnText = root.querySelector('[data-add-button-text]');
    var qtyInput = root.querySelector('[data-bundle-quantity]');
    var propInput = root.querySelector('[data-bundle-property]');
    var addonInput = root.querySelector('[data-addon-input]');

    function currentTier() {
      var checked = root.querySelector('[data-tier-input]:checked');
      return checked ? checked.closest('[data-tier]') : null;
    }

    /* Reads the finished strings off the selected tier and writes them
       into the three nodes that already exist. Nothing is created, so
       whatever the keyboard was focused on stays focused. */
    function refresh(announce) {
      var tier = currentTier();
      if (!tier) return;
      var withAddon = !!(addonInput && addonInput.checked);
      var data = tier.dataset;

      var total = withAddon ? data.totalAddon : data.total;
      var compare = withAddon ? data.compareAddon : data.compare;
      var save = withAddon ? data.saveAddon : data.save;
      var hasCompare = (withAddon ? data.hasCompareAddon : data.hasCompare) === 'true';

      setText(priceEl, total);

      if (compareEl) {
        compareEl.classList.toggle('hidden', !hasCompare);
        setText(compareVal, compare);
      }
      if (saveEl) {
        saveEl.classList.toggle('hidden', !hasCompare);
        if (hasCompare) setText(saveEl, fill(saveEl.dataset.template, '%%PERCENT%%', save));
      }
      if (addBtnText && addBtn) {
        setText(addBtnText, fill(addBtn.dataset.template, '%%PRICE%%', total));
      }

      /* What the form will actually post. */
      if (qtyInput) qtyInput.value = data.count;
      if (propInput) {
        if (data.property) {
          propInput.value = data.property;
          propInput.disabled = false;
        } else {
          propInput.value = '';
          propInput.disabled = true;
        }
      }

      /* Mirror the prototype's class so any rule written against
         `.tier-card.selected` keeps working. It is not what paints the
         card: theme-custom.css pairs every `.selected` rule with a
         `:has(input:checked)` twin, which is why the picker is still
         fully styled with JS switched off. Verified by stripping every
         .selected class at runtime — the card stayed coral. */
      var tiers = root.querySelectorAll('[data-tier]');
      for (var i = 0; i < tiers.length; i++) {
        tiers[i].classList.toggle('selected', tiers[i] === tier);
      }

      if (announce && status && status.dataset.template) {
        setText(status, fill(status.dataset.template, '%%PRICE%%', total));
      }
    }

    root.addEventListener('change', function (event) {
      var target = event.target;
      if (!target) return;

      if (target.matches('[data-tier-input], [data-addon-input]')) {
        refresh(true);
        return;
      }

      /* A variant change moves every price on the page, and every one of
         them is a server-rendered money string. Asking the server again
         is the only answer that cannot drift. */
      if (target.matches('[data-variant-select]')) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', target.value);
        window.location.href = url.toString();
      }
    });

    /* Sync once on load: a back/forward restore can hand us a different
       radio than the one Liquid marked as checked. */
    refresh(false);

    if (!form) return;

    form.addEventListener('submit', function (event) {
      if (!window.fetch || !window.Promise) return; /* let the browser post it */
      event.preventDefault();
      submit();
    });

    function submit() {
      var idField = form.querySelector('[name="id"]');
      if (!idField || !idField.value) return;

      var tier = currentTier();
      var quantity = 1;
      if (tier) {
        quantity = parseInt(tier.dataset.count, 10) || 1;
      } else {
        var manual = form.querySelector('[name="quantity"]');
        quantity = manual ? (parseInt(manual.value, 10) || 1) : 1;
      }

      var item = { id: idField.value, quantity: quantity };
      var bundleName = propertyName(propInput);
      if (tier && tier.dataset.property && bundleName) {
        item.properties = {};
        item.properties[bundleName] = tier.dataset.property;
      }
      var items = [item];

      /* The free book is a real line at its real price. The automatic
         discount in the admin is what makes it free — inventory, taxes
         and the packing slip all stay honest that way. */
      if (tier && tier.dataset.gift === 'true' && root.dataset.giftVariantId) {
        var gift = { id: root.dataset.giftVariantId, quantity: 1 };
        if (root.dataset.giftPropertyName) {
          gift.properties = {};
          gift.properties[root.dataset.giftPropertyName] = root.dataset.giftProperty || '';
        }
        items.push(gift);
      }

      if (addonInput && addonInput.checked && root.dataset.addonVariantId) {
        var addon = { id: root.dataset.addonVariantId, quantity: 1 };
        if (root.dataset.addonPropertyName) {
          addon.properties = {};
          addon.properties[root.dataset.addonPropertyName] = root.dataset.addonProperty || '';
        }
        items.push(addon);
      }

      busy(true);

      /* One cart implementation for the whole theme. theme.js owns the
         drawer, the badge and the free-shipping bar; this only decides
         WHAT goes in — the tier quantity, the properties, and the gift
         and add-on lines the buy box adds alongside the book. The local
         fetch below is only reached if theme.js failed to load. */
      var api = window.Wrenshelf && window.Wrenshelf.cart;

      var request = api && api.add
        ? api.add(items)
        : rawAdd(items);

      request
        .then(function () {
          if (api && api.refresh) return api.refresh();
          return rawCart().then(paintBadge);
        })
        .then(function () {
          busy(false);
          popBadge();
          announceAdded(quantity, items.length > 1);
          if (api && api.open) api.open();
          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: { source: 'product-form' }
          }));
        })
        .catch(function (error) {
          busy(false);
          var message = (error && error.message) || cartErrorMessage();
          if (status) setText(status, message);
          notify(message);
        });
    }

    /* ── the no-theme.js path ──────────────────────────────────────── */

    function rawAdd(items) {
      return fetch(root.dataset.cartAddUrl + '.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: items })
      }).then(function (response) {
        return response.json().then(function (body) {
          if (!response.ok) throw new Error(body.description || body.message || cartErrorMessage());
          return body;
        });
      });
    }

    function rawCart() {
      return fetch(root.dataset.cartUrl + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .catch(function () { return null; });
    }

    function paintBadge(cart) {
      if (!cart) return;
      var badge = document.getElementById('cart-count');
      if (badge) badge.textContent = String(cart.item_count || 0);
    }

    /* The button keeps its width and its label while the request is in
       the air, so the row never reflows under the thumb that just
       pressed it. opacity-70 is a Tailwind utility, generated because
       this file is one of the stylesheet's @source globs. */
    function busy(state) {
      if (!addBtn) return;
      addBtn.disabled = state;
      addBtn.classList.toggle('opacity-70', state);
      if (state) addBtn.setAttribute('aria-busy', 'true');
      else addBtn.removeAttribute('aria-busy');
    }

    function announceAdded(quantity, hasExtras) {
      var template = hasExtras
        ? root.dataset.addedExtrasTemplate
        : root.dataset.addedTemplate;
      var message = fill(fill(template, '%%COUNT%%', quantity), '%%TITLE%%', root.dataset.productTitle || '');
      /* The status line is the accessible announcement and the toast is
         the visible one. Both say the same sentence. */
      if (status) setText(status, message);
      notify(message);
    }
  }

  /* ---------- shared with theme.js when it is there ---------- */

  function notify(message) {
    if (window.Wrenshelf && typeof window.Wrenshelf.showToast === 'function') {
      window.Wrenshelf.showToast(message);
      return;
    }
    toast(message);
  }

  function popBadge() {
    var badge = document.getElementById('cart-count');
    if (!badge) return;
    badge.classList.remove('cart-pop');
    void badge.offsetWidth;
    badge.classList.add('cart-pop');
  }

  function cartErrorMessage() {
    if (window.Wrenshelf && typeof window.Wrenshelf.t === 'function') {
      return window.Wrenshelf.t('cart.error');
    }
    return (window.theme && window.theme.strings && window.theme.strings.cartError) || '';
  }

  /* ---------- gallery ---------- */

  function initGallery(root) {
    var thumbs = root.querySelector('[data-gallery-thumbs]');
    var main = root.querySelector('[data-main-image]');
    if (!thumbs || !main || thumbs.__wrenshelfBound) return;
    thumbs.__wrenshelfBound = true;

    function allThumbs() {
      return thumbs.querySelectorAll('[data-thumb]');
    }

    function select(button) {
      if (!button) return;

      main.src = button.dataset.full;
      main.srcset = button.dataset.srcset || '';
      if (button.dataset.alt) main.alt = button.dataset.alt;

      var all = allThumbs();
      for (var i = 0; i < all.length; i++) {
        var active = all[i] === button;
        all[i].classList.toggle('active', active);
        if (active) all[i].setAttribute('aria-current', 'true');
        else all[i].removeAttribute('aria-current');
      }

      /* The strip scrolls on big galleries — keep the chosen thumb in
         view when the arrows drive it there. block:'nearest' so the
         page itself never jumps. */
      if (button.scrollIntoView) {
        var reduced = document.documentElement.classList.contains('reduce-motion');
        button.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
      }
    }

    thumbs.addEventListener('click', function (event) {
      var button = event.target.closest('[data-thumb]');
      if (!button || !thumbs.contains(button)) return;
      select(button);
    });

    function step(dir) {
      var all = allThumbs();
      if (!all.length) return;
      var current = 0;
      for (var i = 0; i < all.length; i++) {
        if (all[i].classList.contains('active')) { current = i; break; }
      }
      select(all[(current + dir + all.length) % all.length]);
    }

    var prev = root.querySelector('[data-gallery-prev]');
    var next = root.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
  }

  /* ---------- related products ---------- */

  function initRelated() {
    var root = document.querySelector('[data-related-root]');
    if (!root || root.__wrenshelfBound) return;
    root.__wrenshelfBound = true;

    /* Already the recommendations response, or no way to ask for one. */
    if (root.dataset.performed === 'true' || !root.dataset.url || !window.fetch) return;

    fetch(root.dataset.url)
      .then(function (response) { return response.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.getElementById('related');
        var target = root.querySelector('#related');
        /* An empty recommendations response keeps the collection
           fallback rather than emptying the row. */
        if (fresh && target && fresh.children.length) target.innerHTML = fresh.innerHTML;
      })
      .catch(function () { /* the fallback row is already on screen */ });
  }

  /* ---------- boot ---------- */

  function boot() {
    var roots = document.querySelectorAll('[data-product-root]');
    for (var i = 0; i < roots.length; i++) {
      initProduct(roots[i]);
      initGallery(roots[i]);
    }
    initRelated();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* The theme editor rebuilds a section in place; rebind the new nodes. */
  document.addEventListener('shopify:section:load', boot);
})();

/* Thanh mua cố định trên điện thoại — 15/09/2026. */
(function () {
  'use strict';
  if (window.__wsMobileBuyLoaded) return;
  window.__wsMobileBuyLoaded = true;
  var dispose;
  function boot() {
    var root = document.querySelector('[data-product-root]');
    if (!root || root.dataset.wsMobileBuy) return;
    var mainButton = root.querySelector('[data-add-button]');
    var form = root.querySelector('[data-product-add]');
    var price = root.querySelector('[data-price-current]');
    if (!mainButton || !form || !price) return;
    if (dispose) dispose();
    root.dataset.wsMobileBuy = 'true';
    if (!document.getElementById('ws-mobile-buy-style')) {
      var style = document.createElement('style');
      style.id = 'ws-mobile-buy-style';
      style.textContent = '.ws-mobile-buy{display:none}.ws-mobile-buy[hidden]{display:none!important}@media(max-width:767px){body.ws-mobile-buy-ready{padding-bottom:calc(84px + env(safe-area-inset-bottom,0px))}.ws-mobile-buy{position:fixed;inset:auto 0 0;z-index:60;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 16px calc(12px + env(safe-area-inset-bottom,0px));background:#fdf8f2;border-top:1px solid #e6ddd4;box-shadow:0 -4px 20px #2f2a260d;color:#2f2a26}.ws-mobile-buy__details{min-width:0;flex:1}.ws-mobile-buy__price{display:block;font-size:18px;font-weight:700;line-height:1.3}.ws-mobile-buy__selection{display:block;font-size:12px;line-height:1.5;color:#74675c}.ws-mobile-buy__button{flex:0 0 auto;min-height:48px;padding:12px 22px;border:0;border-radius:9999px;background:#f0876b;color:#2f2a26;box-shadow:0 8px 20px -8px #d45f3f8c;font-family:Fredoka,sans-serif;font-weight:600;font-size:16px;line-height:1.2;cursor:pointer}.ws-mobile-buy__button:disabled{opacity:.6;cursor:wait}.ws-mobile-buy__button:focus-visible{outline:3px solid #2f2a26;outline-offset:3px}body.ws-mobile-buy-visible #help-widget{bottom:calc(100px + env(safe-area-inset-bottom,0px))}body.ws-mobile-buy-visible{--shopify-chat-activator-offset:calc(100px + env(safe-area-inset-bottom,0px))}}@media print{.ws-mobile-buy{display:none!important}}';
      document.head.appendChild(style);
    }
    var bar = document.createElement('aside');
    bar.className = 'ws-mobile-buy';
    bar.hidden = true;
    bar.setAttribute('aria-label', 'Quick add to cart');
    var details = document.createElement('div');
    details.className = 'ws-mobile-buy__details';
    var value = document.createElement('span');
    value.className = 'ws-mobile-buy__price';
    var selection = document.createElement('span');
    selection.className = 'ws-mobile-buy__selection';
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'ws-mobile-buy__button';
    var template = mainButton.dataset.template || 'Add to cart';
    var label = template.replace(/\s*[—–-]?\s*%%PRICE%%/g, '').trim();
    button.textContent = label;
    details.appendChild(value);
    details.appendChild(selection);
    bar.appendChild(details);
    bar.appendChild(button);
    document.body.appendChild(bar);
    document.body.classList.add('ws-mobile-buy-ready');
    var media = window.matchMedia('(max-width:767px)');
    var queued = false;
    function setText(node, text) {
      if (node.textContent !== text) node.textContent = text;
    }
    function sync() {
      queued = false;
      if (!root.isConnected) { dispose(); return; }
      setText(value, price.textContent.trim());
      var qty = form.querySelector('[name="quantity"]');
      var count = qty ? parseInt(qty.value, 10) || 1 : 1;
      var addon = root.querySelector('[data-addon-input]:checked');
      setText(selection, count + (count === 1 ? ' book' : ' books') + (addon ? ' + add-on' : ''));
      button.disabled = mainButton.disabled;
      if (mainButton.getAttribute('aria-busy') === 'true') button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
      var header = document.getElementById('site-header');
      var help = document.getElementById('help-bubble');
      var drawer = document.getElementById('cart-drawer');
      var blocked = (drawer && drawer.getAttribute('aria-hidden') === 'false') ||
        (help && help.getAttribute('aria-expanded') === 'true') ||
        (header && header.querySelector('[aria-expanded="true"]'));
      var active = document.activeElement;
      var typing = active && active.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="hidden"]),textarea,select,[contenteditable="true"]');
      // 2026-10-02: show whenever the main button is off screen — above OR below — so price + Add to cart sit
      // on the first screen. While the bar is up, the button only counts as visible once it clears the bar.
      var r = mainButton.getBoundingClientRect();
      var limit = window.innerHeight - (bar.hidden ? 0 : bar.offsetHeight);
      var offscreen = r.bottom <= 0 || r.top >= limit;
      var show = media.matches && offscreen && !blocked && !typing;
      if (bar.hidden === show) bar.hidden = !show;
      if (document.body.classList.contains('ws-mobile-buy-visible') !== !!show) document.body.classList.toggle('ws-mobile-buy-visible', !!show);
    }
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sync);
    }
    button.addEventListener('click', function () {
      if (mainButton.disabled) return;
      mainButton.click();
      sync();
    });
    var observer = new MutationObserver(schedule);
    observer.observe(root, {subtree:true, childList:true, characterData:true, attributes:true, attributeFilter:['disabled','aria-busy']});
    ['site-header','cart-drawer','help-bubble'].forEach(function (id) {
      var node = document.getElementById(id);
      if (node) observer.observe(node, {subtree:true, attributes:true, attributeFilter:['aria-expanded','aria-hidden']});
    });
    root.addEventListener('change', schedule);
    window.addEventListener('scroll', schedule, {passive:true});
    window.addEventListener('resize', schedule);
    window.addEventListener('pageshow', schedule);
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    dispose = function () {
      observer.disconnect();
      root.removeEventListener('change', schedule);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('pageshow', schedule);
      document.removeEventListener('focusin', schedule);
      document.removeEventListener('focusout', schedule);
      bar.remove();
      document.body.classList.remove('ws-mobile-buy-ready','ws-mobile-buy-visible');
      delete root.dataset.wsMobileBuy;
    };
    sync();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target.querySelector('[data-product-root]') && dispose) dispose();
  });
})();
